import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProductProfile from '../components/Product/ProductProfile';
import SalesVolumePerformance from '../components/Product/SalesVolumePerformance';
import CompetitiveIntelligence from '../components/Product/CompetitiveIntelligence';
import VarianceAnalysis from '../components/Product/VarianceAnalysis';
import OpportunityTracker from '../components/Product/OpportunityTracker';
import NotesManager from '../components/Common/NotesManager';

const { FiPackage, FiTrendingUp, FiUsers, FiBarChart, FiTarget } = FiIcons;

const ProductDetails = () => {
  const { productId } = useParams();
  const [activeTab, setActiveTab] = useState('profile');

  const tabs = [
    { id: 'profile', name: 'Product Profile', icon: FiPackage },
    { id: 'performance', name: 'Sales & Volume', icon: FiTrendingUp },
    { id: 'intelligence', name: 'Competitive Intelligence', icon: FiUsers },
    { id: 'variance', name: 'Variance Analysis', icon: FiBarChart },
    { id: 'opportunity', name: 'Opportunity Tracker', icon: FiTarget }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProductProfile productId={productId} />;
      case 'performance':
        return <SalesVolumePerformance productId={productId} />;
      case 'intelligence':
        return <CompetitiveIntelligence productId={productId} />;
      case 'variance':
        return <VarianceAnalysis productId={productId} />;
      case 'opportunity':
        return <OpportunityTracker productId={productId} />;
      default:
        return <ProductProfile productId={productId} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shrink-0">
              <SafeIcon icon={FiPackage} className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {productId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Tablets
              </h1>
              <p className="text-gray-600">Comprehensive product analytics and market intelligence</p>
            </div>
          </div>
          
          {/* Notes Management System */}
          <div className="flex-shrink-0">
            <NotesManager entityId={productId} entityType="product" />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <SafeIcon icon={tab.icon} />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </motion.div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        {renderTabContent()}
      </motion.div>
    </div>
  );
};

export default ProductDetails;